/**
 * Índice ligero de archivos DICOM locales/ZIP: totales por serie
 * sin decodificar píxeles (dicom-parser untilTag).
 */
import dicomParser from 'dicom-parser'

function toUint8(buffer) {
  if (!buffer) return null
  if (buffer instanceof Uint8Array) return buffer
  if (buffer instanceof ArrayBuffer) return new Uint8Array(buffer)
  return null
}

/**
 * @param {ArrayBuffer|Uint8Array} buffer
 * @returns {{ seriesInstanceUID: string, seriesNumber: string }}
 */
export function parseSeriesTagsFromBuffer(buffer) {
  const empty = { seriesInstanceUID: '', seriesNumber: '' }
  const bytes = toUint8(buffer)
  if (!bytes || bytes.byteLength < 128) return empty
  try {
    const ds = dicomParser.parseDicom(bytes, { untilTag: 'x00200011' })
    return {
      seriesInstanceUID: ds.string('x0020000e') || '',
      seriesNumber: ds.string('x00200011') || '',
    }
  } catch (e) {
    try {
      const ds = dicomParser.parseDicom(bytes)
      return {
        seriesInstanceUID: ds.string('x0020000e') || '',
        seriesNumber: ds.string('x00200011') || '',
      }
    } catch (e2) {
      return empty
    }
  }
}

function seriesKey(uid, seriesNumber, fileIndex) {
  if (uid) return `uid:${uid}`
  if (seriesNumber != null && String(seriesNumber) !== '') {
    return `sn:${seriesNumber}`
  }
  return `file:${fileIndex}`
}

/**
 * Obtiene bytes de un item File (local/ZIP) o fs ({ data }).
 * @param {File|Blob|{data?: ArrayBuffer|Uint8Array}} file
 */
export async function readLocalFileBytes(file) {
  if (!file) return null
  if (file.data != null) return toUint8(file.data)
  if (typeof file.arrayBuffer === 'function') {
    const ab = await file.arrayBuffer()
    return toUint8(ab)
  }
  return null
}

/**
 * Cuenta instancias por serie antes de cornerstone.loadImage.
 * @param {Array<File|Blob|*>} files
 * @param {{ onProgress?: (done: number, total: number) => void }} [opts]
 * @returns {Promise<{
 *   totalsByKey: Map<string, number>,
 *   metaByIndex: Array<{ key: string, seriesInstanceUID: string, seriesNumber: string }>,
 *   fileCount: number,
 * }>}
 */
export async function indexLocalFilesBySeries(files, opts = {}) {
  const list = Array.from(files || [])
  const totalsByKey = new Map()
  const metaByIndex = []
  const onProgress = opts.onProgress

  for (let i = 0; i < list.length; i++) {
    const bytes = await readLocalFileBytes(list[i])
    const tags = parseSeriesTagsFromBuffer(bytes)
    const key = seriesKey(tags.seriesInstanceUID, tags.seriesNumber, i)
    totalsByKey.set(key, (totalsByKey.get(key) || 0) + 1)
    metaByIndex.push({
      key,
      seriesInstanceUID: tags.seriesInstanceUID,
      seriesNumber: tags.seriesNumber,
    })
    if (typeof onProgress === 'function' && (i % 8 === 0 || i === list.length - 1)) {
      onProgress(i + 1, list.length)
    }
  }

  return {
    totalsByKey,
    metaByIndex,
    fileCount: list.length,
  }
}

/**
 * Resuelve totalImages para un item a partir del índice previo.
 */
export function resolveSeriesTotalFromIndex(index, seriesInstanceUID, seriesNumber) {
  if (!index || !index.totalsByKey) return null
  const uid = seriesInstanceUID || ''
  const sn = seriesNumber != null ? String(seriesNumber) : ''
  if (uid && index.totalsByKey.has(`uid:${uid}`)) {
    return index.totalsByKey.get(`uid:${uid}`)
  }
  if (sn !== '' && index.totalsByKey.has(`sn:${sn}`)) {
    return index.totalsByKey.get(`sn:${sn}`)
  }
  return null
}
